import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import PasswordChangeForm from '../components/Forms/PasswordChangeForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';

const { FiUser, FiMail, FiPhone, FiCalendar, FiShield, FiLock, FiEdit } = FiIcons;

const Profile = () => {
  const { user, isAdmin } = useAuth();
  const { updateUser } = useCRM();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
  });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      // Only update allowed fields
      const updates = {
        full_name: formData.full_name,
        phone: formData.phone,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('users_crm_2025')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile updated successfully. Please refresh to see changes.');
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* User Info Card */}
        <Card className="md:col-span-2 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 bg-primary-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{user.full_name}</h2>
                <p className="text-gray-500">{user.email}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    isAdmin ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
              <SafeIcon icon={FiEdit} className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-100 pt-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500">Phone Number</p>
              <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2 text-gray-400" />
                {user.phone || 'Not set'}
              </div>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500">Department</p>
              <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiShield} className="h-4 w-4 mr-2 text-gray-400" />
                {user.departments_crm_2025?.name || 'No Department'}
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500">Date of Joining</p>
              <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2 text-gray-400" />
                {user.date_of_joining ? format(new Date(user.date_of_joining), 'MMM dd, yyyy') : 'N/A'}
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500">Account Created</p>
              <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2 text-gray-400" />
                {format(new Date(user.created_at), 'MMM dd, yyyy')}
              </div>
            </div>
          </div>
        </Card>

        {/* Security Card */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiLock} className="h-5 w-5 mr-2" /> Security
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Manage your password and account security settings.
          </p>
          <Button 
            variant="secondary" 
            className="w-full" 
            onClick={() => setIsPasswordModalOpen(true)}
          >
            Change Password
          </Button>
        </Card>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Profile"
        size="md"
      >
        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter phone number"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        size="md"
      >
        <PasswordChangeForm 
          userId={user.id} 
          onCancel={() => setIsPasswordModalOpen(false)} 
          onSuccess={() => setIsPasswordModalOpen(false)} 
        />
      </Modal>
    </div>
  );
};

export default Profile;